// @flow
import { getMainStep } from '../../../models/step'

// Types
import type { Trip } from '../../../models/trip'
import type {
  Providers,
  Amenities,
  Tags,
  NormalizedSearchResults,
} from '../../../models/search'

function tagsBuilder(trips: Array<Trip>): Tags {
  return trips.reduce((result, trip) => {
    const nextResult = { ...result }
    trip.tags.forEach((tag) => {
      const type = tag.tag_type

      if (!nextResult[type]) {
        nextResult[type] = {
          ...tag,
          trips: [],
        }
      }

      nextResult[type].trips.push(trip.id)
    })

    return nextResult
  }, {})
}

function amenitiesBuilder(
  trips: Array<Trip>,
  getMasterStep: Function
): Amenities {
  return trips.reduce((result, trip) => {
    const master = getMasterStep(trip.steps)
    if (!master) return result

    const nextResult = { ...result }
    master.amenities.forEach((amenity) => {
      const { id } = amenity
      if (!nextResult[id]) {
        nextResult[id] = {
          ...amenity,
          trips: [],
        }
      }

      nextResult[id].trips.push(trip.id)
    })

    return nextResult
  }, {})
}

/**
 * Build a provider object
 * @param  {Object} trips Search results information
 * @return {Array} Provider object
 * @private
 */
function providersBuilder(
  trips: Array<Trip>,
  getMasterStep: Function
): Providers {
  return trips.reduce((result, trip) => {
    const master = getMasterStep(trip.steps)
    if (!master) return result

    const nextResult = { ...result }
    const { name } = master.provider

    if (!nextResult[name]) {
      nextResult[name] = {
        logoUrl: master.provider.logoUrl,
        name: master.provider.name,
        rating: parseFloat(master.provider.rating_with_decimals),
        ratingCount: master.provider.rating_count,
        trips: [],
      }
    }

    nextResult[name].trips.push(trip.id)

    return nextResult
  }, {})
}

/**
 * Build a search result object
 * @param  {Trip[]} tripsList A list of unified trips
 * @return {Object} Search result object
 * @private
 */
function searchResultBuilder(tripList: Trip[]): NormalizedSearchResults {
  const trips = tripList.reduce(
    (obj, trip) => ({
      ...obj,
      [trip.id]: trip,
    }),
    {}
  )

  const providers = providersBuilder(tripList, getMainStep)
  const amenities = amenitiesBuilder(tripList, getMainStep)
  const tags = tagsBuilder(tripList)

  const categories = tripList.reduce((result, trip) => {
    const mainStep = getMainStep(trip.steps)
    if (!mainStep) return result

    const newResult = { ...result }
    const key = mainStep.vehicle.type.id
    const tripType = trip.type

    const category = newResult[key] || {
      vehicle_type: key,
      estimatedTime: 0,
      trips: [],
      trip_type: tripType,
    }

    category.trips.push(trip.id)
    newResult[key] = category

    return newResult
  }, {})

  return {
    trips,
    tags,
    providers,
    categories,
    amenities,
  }
}

export default searchResultBuilder
